import { useState, useCallback } from 'react'
import { DepositSplitGrid } from './deposit-split-grid/deposit-split-grid'
import { DepositSplitDialog } from './deposit-split-dialog/deposit-split-dialog'
import { DepositSplitRow } from './deposit-split-grid/deposit-split.types'
import { ReferenceType } from './deposit-split.types'
import { UpdateDepositAssigneds, useUpdateDepositAssignedsMutation } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { v4 as uuidv4 } from 'uuid'
import { DepositSplitDeleteDialog } from './deposit-split-dialog/deposit-split-delete-dialog'

const IS_NEW = 'IS_NEW'

type DepositSplitProps = {
  propertyId: string
  data: DepositSplitRow[]
  usedQuotas: DepositSplitRow[]
  depositDocumentId: string
  loading: boolean
}

export const DepositSplit = (props: DepositSplitProps) => {
  const [data, setData] = useState<DepositSplitRow[]>(props.data)
  const [selected, setSelected] = useState<{ row: DepositSplitRow; operationType: 'edit' | 'split' | 'delete' }>()
  const [updateDepositAssignedM] = useUpdateDepositAssignedsMutation()
  const { id: tenantId } = useTenant()
  const showError = useErrorSnackBar()

  const handleOperation = useCallback(
    (id?: string, operationType?: 'edit' | 'split' | 'delete') => {
      if (id && operationType) {
        const row = data.find((x) => x.id === id)
        if (row) setSelected({ row, operationType })
        else setSelected(undefined)
      } else {
        setSelected(undefined)
      }
    },
    [data],
  )

  const convertRowsToMutationInput = (rows: DepositSplitRow[]): UpdateDepositAssigneds[] => {
    return rows.map<UpdateDepositAssigneds>((d) => ({
      depositAssignedId: !d.id.startsWith(IS_NEW) ? d.id : undefined,
      depositAssignment: {
        billId: d.reference.billId,
        reservationId: d.reference.reservationId,
        reservedResourceId: d.reference.reservedResourceId,
        amount: d.amount,
      },
    }))
  }

  const handleEdit = useCallback(
    (id: string, args: { referenceType: ReferenceType; referenceDescription: string; billId?: string; reservationId?: string; reservedResourceId?: string }) => {
      const rowsToUpdate = data.map<DepositSplitRow>((i) =>
        i.id === id
          ? {
              ...i,
              reference: { type: args.referenceType, description: args.referenceDescription, billId: args.billId, reservationId: args.reservationId, reservedResourceId: args.reservedResourceId },
            }
          : i,
      )
      const dataToUpdate = convertRowsToMutationInput([...rowsToUpdate])
      mutationUpdateRows(dataToUpdate)

      setData([...rowsToUpdate])
      setSelected(undefined)
    },
    [data],
  )

  const mutationUpdateRows = useCallback(async (dataToUpdate: UpdateDepositAssigneds[]) => {
    try {
      const mutationResult = await updateDepositAssignedM({
        variables: {
          tenantId,
          depositDocumentId: props.depositDocumentId,
          depositAssigneds: dataToUpdate,
        },
      })
      if (mutationResult.errors) {
        showError(mutationResult.errors)
      }
    } catch (errors) {
      showError(errors)
    }
  }, [])

  const handleSplit = useCallback(
    async (originalId: string, amount: number, reference: { type: ReferenceType; description: string; billId?: string; reservationId?: string; reservedResourceId?: string }) => {
      const tempData: DepositSplitRow[] = [...data]
      const newUnsavedItem: DepositSplitRow = { id: `${IS_NEW}${uuidv4()}`, reference, amount, currency: 'EUR' }
      const itemToUpdate = tempData.find((i) => i.id === originalId)
      if (itemToUpdate) {
        itemToUpdate.amount = itemToUpdate.amount - amount

        if (itemToUpdate.amount <= 0) {
          tempData.filter((i) => i.id !== originalId)
        } else {
          tempData.map((i) => (i.id === originalId ? itemToUpdate : i))
        }
      }

      const dataToUpdate = convertRowsToMutationInput([...tempData, newUnsavedItem])
      mutationUpdateRows(dataToUpdate)

      setData([...tempData, newUnsavedItem])
      setSelected(undefined)
    },
    [data, tenantId, props.depositDocumentId],
  )

  const handleDelete = useCallback((rowToUpdate: DepositSplitRow[]) => {
    const dataToUpdate = convertRowsToMutationInput([...rowToUpdate])
    mutationUpdateRows(dataToUpdate)
    setData([...rowToUpdate])
    setSelected(undefined)
  }, [])

  return (
    <>
      <DepositSplitGrid //
        propertyId={props.propertyId}
        data={[...data]}
        usedQuotas={[...props.usedQuotas]}
        edit={(id) => handleOperation(id, 'edit')}
        split={(id) => handleOperation(id, 'split')}
        deleteFn={(id) => handleOperation(id, 'delete')}
        loading={props.loading}
      />
      {selected && selected.operationType === 'edit' && (
        <DepositSplitDialog //
          propertyId={props.propertyId}
          id={selected.row.id}
          amount={selected.row.amount}
          quotaReferenceDescription={selected.row.reference.description}
          quotaReferenceType={selected.row.reference.type}
          quotaReferenceId={selected.row.id}
          operationType={'edit'}
          onClose={() => setSelected(undefined)}
          onSave={({ referenceType, referenceDescription, billId, reservationId, reservedResourceId }) => {
            handleEdit(selected.row.id, { referenceType, referenceDescription, billId, reservationId, reservedResourceId })
          }}
        />
      )}

      {selected && selected.operationType === 'split' && (
        <DepositSplitDialog //
          propertyId={props.propertyId}
          id={selected.row.id}
          amount={selected.row.amount}
          quotaReferenceDescription={selected.row.reference.description}
          quotaReferenceType={selected.row.reference.type}
          quotaReferenceId={selected.row.id}
          operationType={'split'}
          onClose={() => setSelected(undefined)}
          onSave={({ referenceType, referenceDescription, billId, reservationId, reservedResourceId, destinationAmount }) => {
            if (destinationAmount) {
              handleSplit(selected.row.id, destinationAmount, { type: referenceType, description: referenceDescription, billId, reservationId, reservedResourceId })
            }
          }}
        />
      )}

      {selected && selected.operationType === 'delete' && (
        <DepositSplitDeleteDialog //
          data={data}
          deleteId={selected.row.id}
          onClose={() => setSelected(undefined)}
          onConfirm={handleDelete}
        />
      )}
    </>
  )
}
